import { useMemo, useState } from 'react'
import {
  KEYWORDS,
  NICHES,
  CATEGORY_ACCENTS,
  type KeywordRow,
  type NicheCategory,
} from '@/data/keywords'

type SortKey = 'priority' | 'volume' | 'difficulty'

function difficultyTone(d: number) {
  if (d <= 15) return { dot: 'bg-emerald-500', text: 'text-emerald-600' }
  if (d <= 25) return { dot: 'bg-amber-500', text: 'text-amber-600' }
  return { dot: 'bg-rose-500', text: 'text-rose-600' }
}

// Priority score favors high volume + low difficulty.
function priorityScore(k: KeywordRow) {
  return k.volume / (k.difficulty || 1)
}

function exportCsv(rows: KeywordRow[]) {
  const headers = ['Keyword', 'Slug', 'Category', 'Volume', 'Difficulty', 'Intent', 'Reason']
  const escape = (s: string) => `"${String(s).replace(/"/g, '""')}"`
  const lines = [
    headers.join(','),
    ...rows.map((r) =>
      [r.keyword, r.slug, r.category, r.volume, r.difficulty, r.intent, r.reason].map((v) => escape(String(v))).join(',')
    ),
  ]
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'junctioncalculator-50-keywords.csv'
  a.click()
  URL.revokeObjectURL(url)
}

export default function KeywordDirectory({
  selectedId,
  onSelect,
}: {
  selectedId?: number
  onSelect?: (k: KeywordRow) => void
}) {
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState<'All' | NicheCategory>('All')
  const [sort, setSort] = useState<SortKey>('priority')

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase()
    let out = KEYWORDS.filter((k) => {
      const matchesCat = category === 'All' || k.category === category
      const matchesQuery =
        !q ||
        k.keyword.toLowerCase().includes(q) ||
        k.slug.toLowerCase().includes(q) ||
        k.reason.toLowerCase().includes(q)
      return matchesCat && matchesQuery
    })
    out = [...out].sort((a, b) => {
      if (sort === 'volume') return b.volume - a.volume
      if (sort === 'difficulty') return a.difficulty - b.difficulty
      return priorityScore(b) - priorityScore(a)
    })
    return out
  }, [query, category, sort])

  return (
    <section id="keywords" className="mx-auto max-w-7xl px-6 pt-16">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">Component B</div>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-900">Vetted 50-Keyword Directory</h2>
          <p className="mt-1 text-sm text-slate-500">
            Handpicked low-difficulty long-tail terms with search volume, difficulty, intent, and strategic rationale.
          </p>
        </div>
        <button
          type="button"
          onClick={() => exportCsv(rows)}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-700"
        >
          Export {rows.length} to CSV
        </button>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-slate-200 p-4 lg:flex-row lg:items-center">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search keywords, slugs, or rationale…"
            className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10"
          />
          <div className="flex flex-wrap items-center gap-2">
            <FilterPill active={category === 'All'} onClick={() => setCategory('All')} label="All" />
            {NICHES.map((n) => (
              <FilterPill
                key={n.category}
                active={category === n.category}
                onClick={() => setCategory(n.category)}
                label={n.category}
                dot={CATEGORY_ACCENTS[n.category].dot}
              />
            ))}
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 outline-none focus:border-slate-400"
          >
            <option value="priority">Sort: Priority</option>
            <option value="volume">Sort: Volume</option>
            <option value="difficulty">Sort: Difficulty</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
                <th className="px-4 py-3 font-semibold">Keyword &amp; Slug</th>
                <th className="px-4 py-3 font-semibold">Category</th>
                <th className="px-4 py-3 font-semibold text-right">Volume</th>
                <th className="px-4 py-3 font-semibold">Difficulty</th>
                <th className="px-4 py-3 font-semibold">Intent</th>
                <th className="px-4 py-3 font-semibold">Strategic Reason</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((k) => {
                const tone = difficultyTone(k.difficulty)
                const accent = CATEGORY_ACCENTS[k.category]
                const isSelected = selectedId === k.id
                return (
                  <tr key={k.id} className={`align-top transition ${isSelected ? 'bg-slate-50' : 'hover:bg-slate-50/60'}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{k.keyword}</div>
                      <div className="mt-0.5 font-mono text-xs text-slate-400">{k.slug}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold ${accent.badge}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${accent.dot}`} />
                        {k.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-slate-700">
                      {k.volume.toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
                        <span className={`font-mono font-semibold ${tone.text}`}>{k.difficulty}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                        {k.intent}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-[260px] text-xs leading-relaxed text-slate-500">{k.reason}</td>
                    <td className="px-4 py-3 text-right">
                      {onSelect ? (
                        <button
                          type="button"
                          onClick={() => onSelect(k)}
                          className={`whitespace-nowrap rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                            isSelected
                              ? 'border-slate-900 bg-slate-900 text-white'
                              : 'border-slate-200 text-slate-700 hover:border-slate-400'
                          }`}
                        >
                          {isSelected ? 'Selected' : 'Select'}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-400">
                    No keywords match your filters.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

function FilterPill({
  active,
  onClick,
  label,
  dot,
}: {
  active: boolean
  onClick: () => void
  label: string
  dot?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
        active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 text-slate-600 hover:border-slate-300'
      }`}
    >
      {dot ? <span className={`h-1.5 w-1.5 rounded-full ${dot}`} /> : null}
      {label}
    </button>
  )
}
